// src/components/features/content/form-steps/AssetItem.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  alpha,
  useTheme,
  Avatar
} from '@mui/material';
import { 
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Image as ImageIcon,
  Movie as VideoIcon,
  AudioFile as AudioIcon,
  UploadFile as FileIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
  BusinessRounded as BusinessIcon
} from '@mui/icons-material';
import { getSignedFileUrl } from '../../../../services/api';
import ImageViewer from '../../../ui/ImageViewer';

/**
 * Component for displaying an asset item in the form with secure URLs
 */
const AssetItem = ({ 
  asset, 
  onRemove, 
  isTemp = false,
  onEdit = null
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    altText: asset.altText || '',
    caption: asset.caption || ''
  });
  
  // Determine if the asset URL is local
  const isLocal = asset.url?.includes('blob') || asset.file;
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };
  
  // Get the effective URL to use for non-image assets
  const getEffectiveUrl = () => {
    if (isLocal) {
      return asset.url || (asset.file && URL.createObjectURL(asset.file));
    }
    return asset.url; // For non-image assets, we'll need to fetch signed URL separately
  };
  
  // Render preview based on asset type
  const renderPreview = () => {
    const effectiveUrl = getEffectiveUrl();
    
    if (asset.type === 'image') {
      return (
        <Box sx={{ 
          height: 120, 
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
        }}>
          <ImageViewer
            file={asset}
            displayType="preview"
            alt={asset.altText || asset.originalname || asset.file?.name || 'Image'}
            sx={{ height: 120, width: '100%' }}
            style={{ objectFit: 'cover' }}
            fallbackToOriginal={true}
          />
        </Box>
      );
    } else if (asset.type === 'video') {
      return (
        <Box sx={{ 
          height: 120, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          position: 'relative'
        }}>
          <VideoIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          {effectiveUrl && (
            <Box 
              component="video" 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.7
              }}
              src={effectiveUrl}
              muted
            />
          )}
        </Box>
      );
    } else if (asset.type === 'audio') {
      return (
        <Box sx={{ 
          height: 120, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.secondary.main, 0.1)
        }}>
          <AudioIcon sx={{ fontSize: 40, color: theme.palette.secondary.main }} />
        </Box>
      );
    } else {
      return (
        <Box sx={{ 
          height: 120, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.info.main, 0.1),
          flexDirection: 'column'
        }}>
          <FileIcon sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
          <Typography variant="caption" align="center" sx={{ px: 1 }}>
            {asset.originalname || asset.file?.name || 'Document'}
          </Typography>
        </Box>
      );
    }
  };
  
  // Get icon for asset type
  const getAssetTypeIcon = () => {
    switch (asset.type) {
      case 'image': return <ImageIcon fontSize="small" />;
      case 'video': return <VideoIcon fontSize="small" />;
      case 'audio': return <AudioIcon fontSize="small" />;
      default: return <FileIcon fontSize="small" />;
    }
  };
  
  // Show edit dialog
  const handleEditClick = () => {
    setEditData({
      altText: asset.altText || '',
      caption: asset.caption || ''
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };
  
  // Save edit data
  const handleSaveEdit = () => {
    if (onEdit) {
      onEdit({
        ...asset,
        altText: editData.altText,
        caption: editData.caption
      });
    }
    setEditDialogOpen(false);
  };
  
  // Open menu
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };
  
  // Close menu
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle asset removal
  const handleRemove = () => {
    if (onRemove) {
      onRemove(asset);
    }
    handleMenuClose();
  };
  
  // Is this a viewable asset?
  const isViewable = Boolean(asset.key || asset.url);
  
  // Handle view action
  const handleView = () => {
    const effectiveUrl = getEffectiveUrl();
    if (effectiveUrl) {
      window.open(effectiveUrl, '_blank');
    }
    handleMenuClose();
  };
  
  return (
    <>
      <Card 
        sx={{ 
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          },
          ...(isTemp ? {
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02)
          } : {})
        }}
      >
        {/* Preview */}
        {renderPreview()}
        
        {/* Temporary badge */}
        {isTemp && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: alpha(theme.palette.primary.main, 0.9),
              color: 'white',
              fontSize: '0.7rem',
              fontWeight: 'medium',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              boxShadow: 1
            }}
          >
            New
          </Box>
        )}
        
        {/* Content */}
        <CardContent sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            mb: 0.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={asset.type}>
                <Box sx={{ mr: 1, display: 'flex' }}>
                  {getAssetTypeIcon()}
                </Box>
              </Tooltip>
              <Typography variant="body2" noWrap sx={{ fontWeight: 'medium', maxWidth: '70%' }}>
                {asset.caption || asset.originalname || asset.file?.name || 'Asset'}
              </Typography>
            </Box>
            
            <IconButton 
              size="small" 
              onClick={handleMenuOpen}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
          
          <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5 }}>
            {asset.originalname || asset.file?.name || ''}
          </Typography>
          
          {asset.size && (
            <Typography variant="caption" color="textSecondary">
              {formatFileSize(asset.size)}
            </Typography>
          )}
          
          {asset.altText && (
            <Tooltip title={asset.altText}>
              <Typography 
                variant="caption" 
                color="textSecondary" 
                noWrap 
                sx={{ 
                  mt: 'auto', 
                  pt: 0.5, 
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <DescriptionIcon fontSize="inherit" sx={{ mr: 0.5, fontSize: '0.875rem' }} />
                {asset.altText}
              </Typography>
            </Tooltip>
          )}
        </CardContent>
      </Card>
      
      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {isViewable && (
          <MenuItem onClick={handleView}>
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            View
          </MenuItem>
        )}
        
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit Details
        </MenuItem>
        
        <MenuItem onClick={handleRemove}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Remove</Typography>
        </MenuItem>
      </Menu>
      
      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Asset Details</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, pb: 1 }}>
            <TextField
              fullWidth
              label="Caption"
              value={editData.caption}
              onChange={(e) => setEditData({ ...editData, caption: e.target.value })}
              margin="normal"
              placeholder="Enter a caption for this asset"
            />
            
            <TextField
              fullWidth
              label="Alt Text"
              value={editData.altText}
              onChange={(e) => setEditData({ ...editData, altText: e.target.value })}
              margin="normal"
              placeholder="Describe this asset for accessibility"
              helperText="Alt text helps users with screen readers understand the content"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit}
            variant="contained"
            
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssetItem;